import React from 'react';
import {
    ActionsAssurance,
    IAssureResume,
    IndicateurFumeur,
    IndicateurResidentCanadien,
    IResumeOffre,
    obtenirLibellePaiementInvalidite,
    obtenirLibelleTypeProtectionAssurance,
    obtenirResumeAssuranceTitreSection,
    ParcoursAssurance,
    QuestionAssurance
} from '../../../hooks';
import { TexteAvecInfoBulle } from '../../texteAvecInfoBulle/TexteAvecInfoBulle';
import {
    BorderRadiusMui,
    ColorHex,
    ContainerMui,
    Hr,
    Police,
    Spacer,
    Tag,
    TagSize,
    TagTheme,
    Typography,
    Variant
} from '../../../styles';
import { Trans, useTranslation } from 'react-i18next';
import { createStyles, makeStyles, useMediaQuery } from '@material-ui/core';
import { EditAssuranceButton } from '../editAssuranceButton/EditAssuranceButton';
import { QuestionAssuranceModalConnected } from './questionAssuranceModal/QuestionAssuranceModal.connected';

export interface IResumeAjoutAssuranceProps {
    resumeOffre: IResumeOffre;
    assures: Array<IAssureResume>;
    isEditButtonDisplayed: boolean;
    parcoursAssurance?: ParcoursAssurance;
    onEditAssurance: (action: ActionsAssurance) => void;
}

const useStyles = makeStyles(() =>
    createStyles({
        fontSize16: {
            fontSize: '16px !important'
        },
        fontSize20: {
            fontSize: '20px !important'
        }
    })
);

const renderQuestionsInfos = (puces: Array<string>, fullSize: boolean) => {
    return (
        <ul
            style={{
                marginBottom: '0',
                paddingRight: '16px',
                width: fullSize ? '100%' : '50%'
            }}
        >
            {puces.map((puce: string, key) => (
                <li key={key}>
                    <Trans>{puce} </Trans>
                </li>
            ))}
        </ul>
    );
};

export const ResumeAjoutAssurance: React.FC<IResumeAjoutAssuranceProps> = ({
    resumeOffre,
    assures = [],
    isEditButtonDisplayed,
    parcoursAssurance,
    onEditAssurance
}) => {
    const classes = useStyles();

    const [t] = useTranslation(['ResumeAssurance']);
    const isMobile = useMediaQuery('(max-width:640px)');

    const renderChoixAssurance = () => {
        const pctCouvertureAssuranceVie = assures[0]?.pctCouvertureAssuranceVie;
        const pctCouvertureAssuranceInvalidite =
            assures[0]?.pctCouvertureAssuranceInvalidite;

        return (
            <>
                {pctCouvertureAssuranceVie != null && (
                    <>
                        <Typography
                            variant={Variant.h5}
                            marginBottom="0.25rem"
                            police={Police.normal}
                            className={classes.fontSize16}
                        >
                            {t('resumeAssuranceVieTitle')}
                        </Typography>
                        <Typography variant={Variant.p} marginBottom="1rem">
                            <strong>
                                {t('Number:percentNoDecimal', {
                                    value: pctCouvertureAssuranceVie
                                })}
                            </strong>
                            {parcoursAssurance === ParcoursAssurance.PARCOURS_3
                                ? t('resumeAssuranceVieTexte')
                                : t('resumeAssuranceVieApMatTexte')}
                        </Typography>
                    </>
                )}
                {pctCouvertureAssuranceInvalidite != null && (
                    <>
                        <Typography
                            variant={Variant.h5}
                            marginBottom="0.25rem"
                            police={Police.normal}
                            className={classes.fontSize16}
                        >
                            {t('resumeAssuranceInvaliditeTitle')}
                        </Typography>
                        <Typography variant={Variant.p} marginBottom="1rem">
                            <strong>
                                {t('Number:percentNoDecimal', {
                                    value: pctCouvertureAssuranceInvalidite
                                })}
                            </strong>
                            {t(obtenirLibellePaiementInvalidite(resumeOffre))}
                        </Typography>
                    </>
                )}
                {/* On n'affiche que s'il est question du PARCOURS_1 */}
                {parcoursAssurance === ParcoursAssurance.PARCOURS_1 && (
                    <>
                        <Typography
                            variant={Variant.h5}
                            marginBottom="0.25rem"
                            police={Police.normal}
                            className={classes.fontSize16}
                        >
                            {t('resumeAssuranceTauxSupplementaire')}
                        </Typography>
                        <Typography variant={Variant.p} marginBottom="1rem">
                            <strong>
                                {t('Number:percent3Decimal', {
                                    value: resumeOffre?.offreChoisie
                                        ?.tauxAdditionnelAssuranceMat
                                })}
                            </strong>
                        </Typography>
                    </>
                )}
            </>
        );
    };

    const renderProfilAssurance = () => {
        return (
            <>
                <Typography
                    variant={Variant.h4}
                    police={Police.bold}
                    marginBottom="1rem"
                >
                    {assures[0]?.prenom + ' ' + assures[0]?.nom}
                </Typography>
                <Tag
                    label={t('tagDemandeAcceptee')}
                    theme={TagTheme.PRIMARY}
                    size={TagSize.SM}
                />
                <Spacer />
                <Spacer all={3} />
                <Typography
                    variant={Variant.h5}
                    police={Police.bold}
                    marginBottom="1rem"
                >
                    {t('profilTitre')}
                </Typography>
                {renderSousTitreAvecInfoBulle(
                    assures[0]?.indicateurResidentCanadien ===
                        IndicateurResidentCanadien.OUI,
                    t('profilSousTitreResidence'),
                    t('profilInfoBulleResidence'),
                    t('profilInfoBulleResidenceTextHorsEcran')
                )}
                <Spacer />
                {renderSousTitreAvecInfoBulle(
                    assures[0]?.indicateurFumeur === IndicateurFumeur.OUI,
                    t('profilSousTitreFumeur'),
                    t('profilInfoBulleFumeur'),
                    t('profilInfoBulleFumeurTextHorsEcran')
                )}
                <Spacer />
                {isEditButtonDisplayed && (
                    <EditAssuranceButton
                        action={ActionsAssurance.PROFIL}
                        libelleHorsEcran={t(
                            'modifierProfilAssuranceTexteHorsEcran'
                        )}
                        onEditAssurance={onEditAssurance}
                    />
                )}
            </>
        );
    };

    const renderQuestionnaireAssurance = () => {
        return (
            <>
                <Typography
                    variant={Variant.h5}
                    police={Police.bold}
                    marginBottom="1rem"
                >
                    {t('questionnaireTitre')}
                </Typography>
                {assures[0].questionsAssurabilites.map(
                    (questionsAssurabilites: QuestionAssurance) => {
                        return (
                            <>
                                <Typography
                                    variant={Variant.p}
                                    width={isMobile ? '100%' : '60%'}
                                    marginBottom="0.5rem"
                                >
                                    <Trans>
                                        {questionsAssurabilites?.question}
                                    </Trans>
                                </Typography>
                                {questionsAssurabilites?.puces.length !== 0 &&
                                questionsAssurabilites.lienModale != null ? (
                                    <QuestionAssuranceModalConnected
                                        lienModale={
                                            questionsAssurabilites.lienModale
                                        }
                                        titreModale={
                                            questionsAssurabilites.titreModale
                                        }
                                        questionsPuces={renderQuestionsInfos(
                                            questionsAssurabilites.puces,
                                            true
                                        )}
                                    />
                                ) : (
                                    renderQuestionsInfos(
                                        questionsAssurabilites.puces,
                                        isMobile
                                    )
                                )}

                                <Typography
                                    variant={Variant.p}
                                    police={Police.bold}
                                    marginBottom="1rem"
                                >
                                    <Trans>
                                        {questionsAssurabilites?.reponse}
                                    </Trans>
                                </Typography>
                            </>
                        );
                    }
                )}
                {isEditButtonDisplayed && (
                    <EditAssuranceButton
                        action={ActionsAssurance.PREASSURABILITE}
                        libelleHorsEcran={t(
                            'modifierQuestionnaireAssuranceTexteHorsEcran'
                        )}
                        onEditAssurance={onEditAssurance}
                    />
                )}
            </>
        );
    };

    const renderSousTitreAvecInfoBulle = (
        indicateurOuiNon: boolean,
        sousTitre: string,
        descriptionInfoBulle: string,
        textHorsEcranInfoBulle: string
    ) => {
        return (
            <>
                <TexteAvecInfoBulle
                    label={t(sousTitre)}
                    labelInfoBulle={descriptionInfoBulle}
                    labelHorsEcranInfoBulle={textHorsEcranInfoBulle}
                />
                <Typography
                    variant={Variant.p}
                    police={Police.bold}
                    marginBottom="0rem"
                >
                    {indicateurOuiNon ? t('indicateurOui') : t('indicateurNon')}
                </Typography>
            </>
        );
    };

    return (
        <ContainerMui
            dataTestId="bloc_ajout_assurance_validation"
            padding={isMobile ? '1rem' : '1.5rem'}
            spacerDistance={6}
            spacerDistanceMobile={5}
        >
            <Typography variant={Variant.h3} police={Police.bold}>
                {t(obtenirResumeAssuranceTitreSection(resumeOffre))}
            </Typography>
            <Typography
                variant={Variant.h4}
                police={Police.bold}
                marginBottom={'1rem'}
                className={classes.fontSize20}
            >
                {t(obtenirLibelleTypeProtectionAssurance(assures))}
            </Typography>
            {renderChoixAssurance()}
            {/* Bouton modifier assurance : Ne pas afficher si MARTINE ( page validation ) */}
            {isEditButtonDisplayed && (
                <EditAssuranceButton
                    action={ActionsAssurance.PROTECTION}
                    libelleHorsEcran={t('modifierProtectionTexteHorsEcran')}
                    spacerRange={6}
                    noSpacer={
                        parcoursAssurance === ParcoursAssurance.PARCOURS_2
                    }
                    onEditAssurance={onEditAssurance}
                />
            )}
            {(parcoursAssurance === ParcoursAssurance.PARCOURS_1 ||
                parcoursAssurance === ParcoursAssurance.PARCOURS_3) && (
                <ContainerMui
                    border={1}
                    borderRadius={BorderRadiusMui.sm}
                    borderColor={ColorHex.graphite}
                    padding={isMobile ? '1rem' : '1.5rem'}
                    noSpacer
                >
                    {renderProfilAssurance()}
                    <Spacer all={2} sm={5} />
                    <Hr backgroundColor={ColorHex.graphite} />
                    <Spacer all={1} sm={3} />
                    {renderQuestionnaireAssurance()}
                </ContainerMui>
            )}
        </ContainerMui>
    );
};
